'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { useGetListsQuery } from 'src/api/redux/lists';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck, faListUl } from '@fortawesome/pro-solid-svg-icons';
import { stripHtml } from 'src/utils/stripHtml';
import {
  GenericNodeComponent,
  GenericSelectorModal,
  ConnectorType,
} from '../shared';
import { useGenericComponent } from '../../hooks/useGenericComponent';
import {
  ProviderListItem,
  providerListNodeConfig,
  providerListModalConfig,
  transformProviderList,
} from './config';

interface ProviderListsComponentProps {
  componentId: string;
  initialProviderLists?: ProviderListItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const ProviderListsComponent: React.FC<ProviderListsComponentProps> = ({
  componentId,
  initialProviderLists = [],
  initialConnector = 'ANY',
  onUpdate,
  onRemove,
  fromPlaceholder,
}) => {
  // Check if we need to hydrate from IDs
  const needsHydration =
    initialProviderLists.length > 0 &&
    (typeof initialProviderLists[0] === 'string' ||
      typeof initialProviderLists[0] === 'number');

  // Fetch all lists (used for both hydration and modal search)
  const { data: listsData, isLoading: isLoadingLists } = useGetListsQuery({});

  // Hydrate IDs to full objects
  const hydratedItems = useMemo<ProviderListItem[]>(() => {
    if (!needsHydration || !listsData?.results) return [];
    const idSet = new Set(
      initialProviderLists.map((item: any) => String(item))
    );
    return listsData.results
      .filter((list: any) => idSet.has(String(list.id)))
      .map(transformProviderList);
  }, [needsHydration, listsData?.results, initialProviderLists]);

  const effectiveInitialItems = needsHydration
    ? hydratedItems
    : initialProviderLists;

  const getItemIds = (items: ProviderListItem[]) => items.map((l) => l.id);

  const {
    items: providerLists,
    connector,
    showModal,
    searchTerm,
    setShowModal,
    setSearchTerm,
    handleSave,
    handleModalClose,
    handleRemoveItem,
    handleConnectorChange,
  } = useGenericComponent<ProviderListItem>({
    componentId,
    type: 'provider_list',
    reduxDataField: 'value',
    getItemIds,
    initialItems: effectiveInitialItems,
    initialConnector,
    onUpdate,
    onRemove,
    fromPlaceholder,
  });

  // Close modal that was auto-opened during hydration once items resolve
  useEffect(() => {
    if (needsHydration && effectiveInitialItems.length > 0) {
      setShowModal(false);
    }
  }, [needsHydration, effectiveInitialItems.length, setShowModal]);

  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Filter lists locally for modal search
  const searchResults: ProviderListItem[] = useMemo(() => {
    const allLists = listsData?.results || [];
    const transformed = allLists.map(transformProviderList);

    if (!debouncedSearch) return transformed;

    const searchLower = debouncedSearch.toLowerCase();
    return transformed.filter((list) =>
      list.label.toLowerCase().includes(searchLower)
    );
  }, [listsData?.results, debouncedSearch]);

  // Custom results renderer that shows all lists immediately (no search-first gate)
  const renderResults = useCallback(
    ({
      selectedItems,
      onSelect,
      isLoading,
      searchTerm: currentSearchTerm,
    }: {
      results: ProviderListItem[];
      selectedItems: ProviderListItem[];
      onSelect: (item: ProviderListItem) => void;
      onDeselect: (item: ProviderListItem) => void;
      isLoading: boolean;
      searchTerm: string;
    }) => {
      if (isLoading) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in duration-300">
              <div className="animate-pulse text-neutral-400">
                Loading provider lists...
              </div>
            </div>
          </div>
        );
      }

      if (searchResults.length === 0) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex flex-col items-center gap-3">
                <Icon
                  icon={faListUl}
                  className="w-12 h-12 text-neutral-300"
                />
                <div className="flex flex-col items-center gap-1">
                  <div className="text-sm">
                    {currentSearchTerm
                      ? `No provider lists found for "${currentSearchTerm}"`
                      : 'No provider lists available'}
                  </div>
                  {currentSearchTerm && (
                    <div className="text-xs text-neutral-400">
                      Try adjusting your search terms
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="h-full overflow-y-auto border border-neutral-200 rounded-lg bg-white">
          {searchResults.map((item) => {
            const isSelected = selectedItems.some((s) => s.id === item.id);
            return (
              <div
                key={item.id}
                onClick={() => onSelect(item)}
                data-testid={`provider-list-option-${item.id}`}
                className={`p-4 border-b border-neutral-100 last:border-b-0 cursor-pointer transition-all duration-300 hover:bg-neutral-50 ${
                  isSelected ? 'bg-lime-50 hover:bg-lime-100' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-150 ${
                        isSelected
                          ? 'border-lime-600 bg-lime-600'
                          : 'border-neutral-300'
                      }`}
                    >
                      {isSelected && (
                        <Icon
                          icon={faCheck}
                          className="w-2.5 h-2.5 text-white"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium text-sm truncate ${
                          isSelected ? 'text-lime-800' : 'text-neutral-900'
                        }`}
                      >
                        {stripHtml(item.label)}
                      </div>
                      {item.num_items != null && (
                        <div className="text-xs text-neutral-500">
                          {item.num_items} providers
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    [searchResults]
  );

  // Config with custom renderer
  const modalConfigWithRender = useMemo(
    () => ({
      ...providerListModalConfig,
      renderResultsSection: renderResults,
    }),
    [renderResults]
  );

  // Show loading state while hydrating
  if (needsHydration && isLoadingLists) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-neutral-200 rounded-full text-xs text-neutral-500">
        Loading provider lists...
      </span>
    );
  }

  return (
    <>
      {providerLists.length > 0 && (
        <GenericNodeComponent
          config={providerListNodeConfig}
          items={providerLists}
          connector={connector}
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
          onConnectorChange={handleConnectorChange}
        />
      )}

      <GenericSelectorModal
        config={modalConfigWithRender}
        isOpen={showModal}
        selectedItems={providerLists}
        connector={connector}
        isEditing={providerLists.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoadingLists}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default ProviderListsComponent;
